import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { CustomButton } from './CustomButton';
import { getButtonColor } from '../../theme/themeUtils';

function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return (
    /importing a module script failed/i.test(message) ||
    /loading chunk \d+ failed/i.test(message) ||
    /failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message)
  );
}

function ChunkLoadErrorFallback({ onRetry }: { onRetry: () => void }) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '50vh',
        px: 2,
        backgroundColor: theme.palette.background.default,
      }}
    >
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, textAlign: 'center' }}>
        Page couldn&apos;t load
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center', maxWidth: 320 }}>
        This can happen after an update or on a slow connection. Refreshing usually fixes it.
      </Typography>
      <CustomButton
        variant="contained"
        onClick={onRetry}
        startIcon={<RefreshIcon />}
        sx={{
          backgroundColor: getButtonColor(theme),
          '&:hover': { backgroundColor: getButtonColor(theme), opacity: 0.9 },
        }}
      >
        Refresh page
      </CustomButton>
    </Box>
  );
}

interface ChunkLoadErrorBoundaryProps {
  children: React.ReactNode;
}

interface ChunkLoadErrorBoundaryState {
  error: unknown | null;
}

/**
 * Error boundary that catches chunk load failures (e.g. "Importing a module script failed")
 * and shows a friendly message with a refresh button instead of a blank crash.
 */
export class ChunkLoadErrorBoundary extends React.Component<
  ChunkLoadErrorBoundaryProps,
  ChunkLoadErrorBoundaryState
> {
  state: ChunkLoadErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: unknown): Partial<ChunkLoadErrorBoundaryState> {
    return { error };
  }

  componentDidCatch(error: unknown, errorInfo: React.ErrorInfo): void {
    // Only treat as chunk load error if message matches; otherwise we might want to rethrow
    // or let a parent boundary handle it. For now we show fallback for any error in this tree.
    if (!isChunkLoadError(error)) {
      console.error('Route error (non-chunk):', error, errorInfo);
    }
  }

  render(): React.ReactNode {
    if (this.state.error != null) {
      return (
        <ChunkLoadErrorFallback onRetry={() => window.location.reload()} />
      );
    }
    return this.props.children;
  }
}
